import { useState, isValidElement, cloneElement } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

type BtnVariant = React.ComponentProps<typeof Button>['variant']
type BtnSize = React.ComponentProps<typeof Button>['size']

export interface Props {
  /** Opção A: texto para o botão padrão do trigger */
  triggerText?: string
  triggerVariant?: BtnVariant
  triggerSize?: BtnSize
  triggerClassName?: string
  disabled?: boolean

  /** Opção B: trigger customizado (qualquer ReactNode) */
  trigger?: React.ReactNode

  /** Conteúdo de confirmação */
  confirmText: string
  /** Nome novo */
  confirmBtnText?: string
  /** Alias compatível com uso antigo */
  successBtnText?: string
  cancelBtnText?: string

  /** Aparência dos botões do popover */
  confirmBtnVariant?: BtnVariant // default: 'destructive'
  cancelBtnVariant?: BtnVariant // default: 'outline'

  /** Nome novo */
  onConfirm?: () => void | Promise<void>
  /** Alias compatível com uso antigo */
  onSuccess?: () => void | Promise<void>
  onCancel?: () => void
}

export default function ButtonConfirm({
  // Trigger default (texto) – opcional quando usar `trigger`
  triggerText,
  triggerVariant = 'default',
  triggerSize = 'sm',
  triggerClassName,
  disabled,
  trigger,

  // Conteúdo
  confirmText,
  confirmBtnText,
  successBtnText, // alias
  cancelBtnText = 'Cancelar',

  // Estilos dos botões internos
  confirmBtnVariant = 'destructive',
  cancelBtnVariant = 'outline',

  // Handlers
  onConfirm,
  onSuccess, // alias
  onCancel
}: Props) {
  const [open, setOpen] = useState(false)

  const finalConfirmHandler = onConfirm ?? onSuccess
  const finalConfirmLabel = confirmBtnText ?? successBtnText ?? 'Confirmar'

  const handleConfirm = async () => {
    await finalConfirmHandler?.()
    setOpen(false)
  }

  const handleCancel = () => {
    onCancel?.()
    setOpen(false)
  }

  // Se veio um trigger customizado, preserva onClick se existir e injeta abertura do popover
  const triggerNode = (() => {
    if (trigger) {
      if (isValidElement(trigger)) {
        // Só injeta onClick extra que abre o popover; mantém props originais
        const originalOnClick = (trigger.props as any)?.onClick
        return cloneElement(trigger as any, {
          onClick: (e: any) => {
            originalOnClick?.(e)
            setOpen(true)
          }
        })
      }
      return trigger
    }

    // Fallback: botão padrão do componente
    return (
      <Button
        variant={triggerVariant}
        size={triggerSize}
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {triggerText ?? 'Abrir'}
      </Button>
    )
  })()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>

      <PopoverContent className="w-80">
        <div className="text-sm">{confirmText}</div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant={cancelBtnVariant} size="sm" onClick={handleCancel}>
            {cancelBtnText}
          </Button>
          <Button variant={confirmBtnVariant} size="sm" onClick={handleConfirm} disabled={!finalConfirmHandler}>
            {finalConfirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
